import { FC } from 'react';

import { RegisterOptions, UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
	DropDownCustomField,
	ICustomField,
	NumberCustomField,
	TextCustomField,
	WidgetInputs,
} from '../../@types/types';
import { creditCardCheckRegex } from '../../shared/constants';
import { isCustomDropDownField, isCustomNumberField, isCustomTextField } from '../../shared/utils';
import { SelectField, TextField } from '../fields';
import { OptionType } from '../select/Select';

export type CustomFieldsProps = {
	customFields: ICustomField[];
	customFieldOptions: Partial<Record<string, RegisterOptions<any>>>;
	errorMessages: Record<string, string>;
	register: UseFormRegister<WidgetInputs & { [key: string]: string }>;
};

export const CustomFields: FC<CustomFieldsProps> = ({ customFields, errorMessages, customFieldOptions, register }) => {
	const { t: translate } = useTranslation();
	const getTextField = (field: TextCustomField) => {
		const { Key, Label, Placeholder } = field;
		const options = customFieldOptions[Key];

		return (
			<TextField
				key={Key}
				{...register(Key, {
					...options,
					maxLength: field.MaxLength,
					minLength: field.MinLength,
					validate: value => (creditCardCheckRegex.test(value) ? 'errors.suspectedCreditCard' : undefined),
				})}
				errorMessage={errorMessages[Key]}
				id={Label}
				invalid={!!errorMessages[Key]}
				placeholder={Placeholder}
				required={field.IsRequired}
				title={field.Label}
				type="text"
			/>
		);
	};
	const getNumberField = (field: NumberCustomField) => {
		const { Key, Label, Placeholder } = field;
		const options = customFieldOptions[Key];
		return (
			<TextField
				key={Key}
				{...register(Key, {
					...options,
					maxLength: field.MaxLength,
					minLength: field.MinLength,
					min: { value: 0, message: 'errors.minNumericValue' },
					validate: value => {
						if (creditCardCheckRegex.test(value)) {
							return 'errors.suspectedCreditCard';
						}
						if (value.includes('.')) {
							return 'errors.customFieldInvalid';
						}
						return undefined;
					},
				})}
				errorMessage={errorMessages[Key]}
				id={Label}
				inputMode="numeric"
				invalid={!!errorMessages[Key]}
				placeholder={Placeholder}
				required={field.IsRequired}
				title={field.Label}
				type="text"
			/>
		);
	};
	const getDropDownField = (field: DropDownCustomField) => {
		const { Key, Label } = field;
		let options: OptionType[] = field.Options.map(option => ({
			label: option.Label,
			value: option.Value,
		}));

		if (!field.DefaultOptionValue) {
			options = [
				{
					label: translate('funds.chooseOption', { label: field.Label }),
					value: '',
				},
				...options,
			];
		}

		return (
			<SelectField
				key={Key}
				{...register(Key, customFieldOptions[Key])}
				defaultValue={field.DefaultOptionValue ?? undefined}
				errorMessage={errorMessages[Key]}
				id={Label}
				invalid={!!errorMessages[Key]}
				options={options}
				required={field.IsRequired}
				title={field.Label}
			/>
		);
	};

	return (
		<div>
			{customFields.map(field => {
				switch (field.Type) {
					case isCustomTextField:
						return getTextField(field as TextCustomField);
					case isCustomNumberField:
						return getNumberField(field as NumberCustomField);
					case isCustomDropDownField:
						return getDropDownField(field as DropDownCustomField);
					default:
						return null;
				}
			})}
		</div>
	);
};
